import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';

/** CSS custom properties used as fallbacks when env(safe-area-inset-*) is 0 on Android WebView. */
const INSET_VARS = [
  '--safe-area-inset-top',
  '--safe-area-inset-bottom',
  '--safe-area-inset-left',
  '--safe-area-inset-right',
] as const;

let listenersBound = false;
type ViewportInsets = { top: number; bottom: number; left: number; right: number };
const iosInsetCache = new Map<string, ViewportInsets>();

function viewportOrientation() {
  return window.innerWidth > window.innerHeight ? 'landscape' : 'portrait';
}

function readCssSafeAreaInsets(): ViewportInsets {
  const probe = document.createElement('div');
  probe.style.cssText = [
    'position:fixed',
    'visibility:hidden',
    'pointer-events:none',
    'padding-top:env(safe-area-inset-top,0px)',
    'padding-bottom:env(safe-area-inset-bottom,0px)',
    'padding-left:env(safe-area-inset-left,0px)',
    'padding-right:env(safe-area-inset-right,0px)',
  ].join(';');
  document.body.appendChild(probe);
  const style = window.getComputedStyle(probe);
  const insets = {
    top: Number.parseFloat(style.paddingTop) || 0,
    bottom: Number.parseFloat(style.paddingBottom) || 0,
    left: Number.parseFloat(style.paddingLeft) || 0,
    right: Number.parseFloat(style.paddingRight) || 0,
  };
  probe.remove();
  return insets;
}

function readViewportInsets() {
  const vv = window.visualViewport;
  if (!vv) return null;

  const top = Math.max(0, Math.round(vv.offsetTop));
  const bottom = Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop));
  const left = Math.max(0, Math.round(vv.offsetLeft));
  const right = Math.max(0, Math.round(window.innerWidth - vv.width - vv.offsetLeft));

  return { top, bottom, left, right };
}

function applyViewportInsets() {
  const root = document.documentElement;

  // Quick Look / the iOS document viewer can briefly return zero for every env()
  // safe-area value while it is being dismissed. Preserve the largest valid values
  // seen in each orientation so the header cannot jump underneath the notch.
  if (Capacitor.getPlatform() === 'ios') {
    const key = viewportOrientation();
    const measured = readCssSafeAreaInsets();
    const previous = iosInsetCache.get(key);
    const insets = previous
      ? {
          top: Math.max(previous.top, measured.top),
          bottom: Math.max(previous.bottom, measured.bottom),
          left: Math.max(previous.left, measured.left),
          right: Math.max(previous.right, measured.right),
        }
      : measured;

    iosInsetCache.set(key, insets);
    root.style.setProperty(INSET_VARS[0], `${insets.top}px`);
    root.style.setProperty(INSET_VARS[1], `${insets.bottom}px`);
    root.style.setProperty(INSET_VARS[2], `${insets.left}px`);
    root.style.setProperty(INSET_VARS[3], `${insets.right}px`);
    return;
  }

  const insets = readViewportInsets();
  if (!insets) return;

  root.style.setProperty(INSET_VARS[0], `${insets.top}px`);
  root.style.setProperty(INSET_VARS[1], `${insets.bottom}px`);
  root.style.setProperty(INSET_VARS[2], `${insets.left}px`);
  root.style.setProperty(INSET_VARS[3], `${insets.right}px`);
}

async function restoreNativeChrome() {
  const platform = Capacitor.getPlatform();

  try {
    // These APIs are Android-specific. Keeping them out of the iOS call path
    // ensures a rejected overlay call cannot prevent the iOS icon style refresh.
    if (platform === 'android') {
      await StatusBar.setOverlaysWebView({ overlay: false });
      await StatusBar.setBackgroundColor({ color: '#0f172a' });
    }
    // Android uses a dark status-bar background; iOS uses the page/header safe area.
    await StatusBar.setStyle({
      style: platform === 'android' ? Style.Light : Style.Dark,
    });
  } catch {
    /* StatusBar plugin unavailable in web preview */
  }

  // External viewers can return with stale viewport geometry. Re-read it over
  // two frames and notify layout code after the native activity is foregrounded.
  window.requestAnimationFrame(() => {
    applyViewportInsets();
    window.requestAnimationFrame(() => {
      applyViewportInsets();
      window.dispatchEvent(new Event('resize'));
    });
  });
}

function bindViewportInsetListeners() {
  if (listenersBound) return;
  listenersBound = true;

  const vv = window.visualViewport;
  const refresh = () => {
    window.requestAnimationFrame(applyViewportInsets);
  };

  vv?.addEventListener('resize', refresh);
  vv?.addEventListener('scroll', refresh);
  window.addEventListener('resize', refresh);
  window.addEventListener('orientationchange', () => {
    window.setTimeout(() => void restoreNativeChrome(), 120);
  });
  window.addEventListener('pageshow', () => void restoreNativeChrome());
  window.addEventListener('focus', () => void restoreNativeChrome());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void restoreNativeChrome();
  });

  void import('@capacitor/app')
    .then(({ App }) => {
      App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) void restoreNativeChrome();
      });
      App.addListener('resume', () => void restoreNativeChrome());
    })
    .catch(() => {
      /* App plugin optional in web builds */
    });
}

/**
 * Keep APK content out of the status-bar notch and bottom gesture area.
 * Android WebView often reports env(safe-area-inset-*) as 0 even with viewport-fit=cover.
 */
export async function initNativeSafeArea(): Promise<void> {
  if (typeof window === 'undefined' || !Capacitor.isNativePlatform()) return;

  document.documentElement.classList.add('cap-native');
  bindViewportInsetListeners();
  await restoreNativeChrome();
}
